import { parseISO } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import type { IANATimezoneIdentifier } from './types'
import { ensureValidTimeZoneIdentifier } from './validateArguments'

/**
 * Convert a floating timestamp like `"2020-01-01T00:00"` to a `Date` in the
 * `timezoneIdentifier` time zone.
 *
 * @example
 * floatingTimestampToDateInZone("2020-01-01T00:00", "America/Los_Angeles")
 * //=> new Date("2020-01-01T08:00:00.000Z")
 *
 * @note Although this is just a simple wrapper around `zonedTimeToUtc`, it’s
 *   worth having the abstraction so we can change implementations, and the
 *   name is clearer.
 * @note This has unspecified behavior when dealing with times that don’t
 *   ‘exist’ during a daylight saving transition. (For example, in the United
 *   States 2:00–2:59 AM don’t ‘exist’ when daylight saving starts because the
 *   clock jumps to 3 AM.)
 */
export function floatingTimestampToDateInZone(
  timestamp: string,
  timezoneIdentifier: IANATimezoneIdentifier
): Date {
  if (!timestamp.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/)) {
    throw new Error(`Invalid timestamp: ${timestamp}`)
  }

  ensureValidTimeZoneIdentifier(timezoneIdentifier)

  return fromZonedTime(parseISO(timestamp), timezoneIdentifier)
}
